import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { User } from 'oidc-client-ts';
import { useAuth } from 'react-oidc-context';
import { PopupUtils } from '../../lib/popup';
import { OfficeDialogUtils } from '../../lib/officeDialog';
import { Button } from '../../ui/button';
import { ArrowRight } from 'lucide-react';
import EnvironmentSelection from './EnvironmentSelection';
import { getCachedConfig, getSelectedRegion, getCustomAppUrl, hasEnvironmentSelection, isLocalDevelopment, clearEnvironmentSelection, } from '../../lib/service-discovery';
import { useOfficeHost } from '../../lib/useOfficeHost';
import { getOfficeHostType } from '../../lib/host-adapters';
function AuthNavigator() {
    const auth = useAuth();
    const { hostType: officeHostType, isOutlook, isPowerPoint } = useOfficeHost();
    const [showEnvironmentSelection, setShowEnvironmentSelection] = useState(false);
    // Convert Office.HostType to OfficeHostType
    const hostType = getOfficeHostType(officeHostType);
    const getDescription = () => {
        if (isOutlook) {
            return 'Boost your email productivity with AI-powered drafting, summarization, and intelligent responses.';
        }
        if (isPowerPoint) {
            return 'Enhance your presentations with AI-powered analysis, content suggestions, and intelligent slide generation.';
        }
        return 'Enhance your documents with AI-powered analysis, writing assistance, and intelligent content generation.';
    };
    useEffect(() => {
        // Skip environment selection for localhost development
        if (isLocalDevelopment()) {
            console.log('[AuthNavigator] Skipping environment selection for localhost');
            return;
        }
        // Check if environment is selected
        if (!hasEnvironmentSelection() && !auth.isAuthenticated) {
            console.log('[AuthNavigator] No environment selected, showing selection');
            setShowEnvironmentSelection(true);
        }
    }, [auth.isAuthenticated]);
    const handleEnvironmentSelect = () => {
        // Environment is already saved by EnvironmentSelection component
        // This callback is only called when we're already on the correct origin
        // (EnvironmentSelection handles the redirect when origin differs)
        setShowEnvironmentSelection(false);
        // Reload the page to trigger bootstrap with the new environment
        console.log('[AuthNavigator] Environment selected, reloading page...');
        window.location.reload();
    };
    const openPopup = async () => {
        console.log('[AuthNavigator] openPopup called');
        console.log('[AuthNavigator] auth.isAuthenticated:', auth.isAuthenticated);
        try {
            if (!auth.isAuthenticated) {
                const config = getCachedConfig();
                if (!config) {
                    console.error('[AuthNavigator] No cached config available');
                    return;
                }
                // Build popup URL with environment params so popup can bootstrap correctly
                // This is needed because Office dialogs may not share localStorage with the taskpane
                const popupUrl = new URL(config.popupRedirectUri);
                const selectedRegion = getSelectedRegion();
                const customAppUrl = getCustomAppUrl();
                if (selectedRegion) {
                    popupUrl.searchParams.set('_region', selectedRegion);
                }
                if (customAppUrl) {
                    popupUrl.searchParams.set('_customUrl', customAppUrl);
                }
                const url = popupUrl.toString();
                console.log('[AuthNavigator] Popup URL:', url);
                console.log('[AuthNavigator] Config:', {
                    authority: config.authority,
                    clientId: config.clientId,
                });
                // Check if we're in Office desktop environment
                const isDesktop = OfficeDialogUtils.isOfficeDesktop();
                console.log('[AuthNavigator] isDesktop:', isDesktop);
                if (isDesktop) {
                    // Use Office Dialog API for desktop apps
                    console.log('[AuthNavigator] Using Office Dialog API for desktop');
                    try {
                        console.log('[AuthNavigator] Calling OfficeDialogUtils.openDialog...');
                        const result = await OfficeDialogUtils.openDialog(url, 60, 75);
                        console.log('[AuthNavigator] Dialog returned result:', {
                            source: result.source,
                            hasUser: !!result.user,
                        });
                        // Handle the authentication result
                        if (result.source === 'oidc-client' && result.user) {
                            const { authority, clientId } = config;
                            const storageKey = `oidc.user:${authority}:${clientId}`;
                            console.log('[AuthNavigator] Storing user in localStorage with key:', storageKey);
                            localStorage.setItem(storageKey, result.user);
                            console.log('[AuthNavigator] User stored, calling auth.events.load...');
                            const user = User.fromStorageString(result.user);
                            await auth.events.load(user, true);
                            console.log('[AuthNavigator] auth.events.load completed');
                        }
                        else {
                            console.warn('[AuthNavigator] Unexpected result from dialog:', result);
                        }
                    }
                    catch (dialogError) {
                        console.error('[AuthNavigator] Office Dialog authentication failed:', dialogError);
                        // Optionally show error to user
                    }
                }
                else {
                    // Use standard popup for browser environments
                    console.log('[AuthNavigator] Using standard popup for browser');
                    const popup = PopupUtils.center({
                        width: 800,
                        height: 640,
                        toolbar: false,
                        location: false,
                    });
                    const windowRef = window.open(url, 'authPopup', PopupUtils.serialize(popup));
                    if (windowRef) {
                        console.log('[AuthNavigator] Popup window opened');
                        windowRef.focus();
                    }
                    else {
                        console.error('[AuthNavigator] Failed to open popup window');
                    }
                }
            }
            else {
                console.log('[AuthNavigator] Already authenticated, skipping popup');
            }
        }
        catch (error) {
            console.error('[AuthNavigator] Authentication error:', error);
        }
    };
    if (showEnvironmentSelection) {
        return (_jsx(EnvironmentSelection, { onEnvironmentSelect: handleEnvironmentSelect, hostType: hostType }));
    }
    return (_jsx("div", { className: 'h-screen w-screen flex items-center justify-center', children: _jsxs("div", { className: `h-full w-full bg-[url('/assets/airia-addon-bg.png')] bg-no-repeat bg-cover bg-right flex flex-col justify-center items-center gap-6 p-4`, children: [_jsxs("div", { className: 'flex flex-col gap-2 items-center', children: [_jsx("img", { src: '/assets/airia-logo-full.png', alt: 'Airia logo', className: 'w-[100px]' }), _jsx("h3", { className: 'text-sm text-[#3d3d39]', children: "Enterprise AI Simplified" })] }), _jsx("p", { className: 'text-sm text-[#3d3d39] text-center max-w-[280px]', children: getDescription() }), _jsxs(Button, { variant: 'default', disabled: auth.isAuthenticated, onClick: () => openPopup(), className: 'w-full bg-[#007AFF] text-white rounded-full', children: [_jsx("span", { className: 'mr-1', children: "Login" }), _jsx(ArrowRight, { className: 'h-4 w-4' })] }), _jsxs("p", { className: 'text-sm text-[#3d3d39]', children: ["Don't have an account?", ' ', _jsx("a", { href: "https://airia.com/register/", target: "_blank", rel: "noopener noreferrer", className: 'text-[#007AFF] hover:underline font-medium', children: "Sign Up" })] }), _jsx("button", { onClick: () => {
                        clearEnvironmentSelection();
                        setShowEnvironmentSelection(true);
                    }, className: 'text-sm text-[#6b7280] hover:text-[#007AFF] hover:underline mt-2', children: "Start Over" })] }) }));
}
export default AuthNavigator;
